import { match } from "ts-pattern";

import type { FormControl, FormElement } from "../collectors/form/plainForm.js";
import type { Assessment } from "../types/assessment.js";
import {
  CATEGORY_KEYWORD,
  GROUP_CATEGORIES,
  type GroupCategory,
} from "./authoring.js";
import { isNotAnswered } from "./notAnswered.js";
import {
  commentTargetOf,
  type PublishedInstrumentSchema,
} from "./publishedSchema.js";
import { levelOf, scaleOf, type RatingScale } from "./ratingScale.js";

type Response = Assessment["submissions"][number]["responses"][number];

/** The scale an item's control offers; a text or date control offers none, and reads as rated. */
const scaleOfControl = (control: FormControl): RatingScale =>
  match(control)
    .with({ kind: "choice" }, ({ options }) =>
      scaleOf(options.map(({ value }) => value)),
    )
    .with({ kind: "text" }, { kind: "date" }, (): RatingScale => "rating")
    .exhaustive();

/** One assessment as the summary reads it: its pinned schema, our form for it, and what it said. */
export type SummaryInput = {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  responses: readonly Response[];
};

/**
 * How an item moved between the earlier assessment and this one.
 *
 * For needs, strengths and the caregiver's needs, which are shown where they
 * call for action: `new` was not shown before, `escalated` and `eased` moved
 * up or down within what is shown, `weakened` is a strength that moved up, and
 * `continuing` stayed where it was. `resolved` and `built` are no longer shown,
 * the need having fallen below action or the strength having become one to use.
 *
 * For history, which never calls for action: `same`, `changed` and `new`.
 *
 * `not_rated` is an item shown before that this assessment does not rate, and
 * `not_answered` one shown before that this assessment sent not answered.
 */
export type ItemChange =
  | "new"
  | "escalated"
  | "eased"
  | "weakened"
  | "continuing"
  | "resolved"
  | "built"
  | "same"
  | "changed"
  | "not_rated"
  | "not_answered";

export type SummaryItem = {
  group: string;
  item: string;
  title: string;
  /** The title of the domain the item is in. */
  domain: string;
  /** Where the item comes in our form, so items sort as the form asks them. */
  position: number;
  scale: RatingScale;
  /** Absent where this assessment does not rate the item. */
  rating?: number | undefined;
  /** Present only in a comparison, and absent there where the earlier assessment did not rate it. */
  previous?: number | undefined;
  /** Present only in a comparison. */
  change?: ItemChange | undefined;
  /** In a comparison, where the earlier assessment sent this item not answered. */
  notAnsweredBefore?: true | undefined;
};

/** An item this assessment rated, which is every item a column or strip shows. */
export type ShownItem = SummaryItem & { rating: number };

/** An item the earlier assessment showed and this one does not. */
export type DepartedItem = SummaryItem & { previous: number };

/** One kind of domain, with the items it shows and, in a comparison, those it no longer shows. */
export type SummaryColumn = {
  category: GroupCategory;
  shown: ShownItem[];
  /** Empty unless comparing. */
  departed: DepartedItem[];
  /** How many the earlier assessment showed; absent unless comparing. */
  previousCount?: number | undefined;
};

export type AssessmentSummaryView = {
  comparing: boolean;
  /** One per kind of domain the instruments have, in `COLUMN_ORDER`. */
  columns: SummaryColumn[];
  /** Needs rated 1: worth watching, not yet calling for action. */
  background: ShownItem[];
  /** Strengths rated 0 or 1, to use in a plan. */
  assets: ShownItem[];
  /** The caregiver's resources, rated 0 or 1. */
  caregiverResources: ShownItem[];
  /** How many items this assessment rated with a number. */
  rated: number;
  /** In our form's order, then any our form does not ask. */
  notAnsweredByDomain: NotAnsweredInDomain[];
};

/** How many items of one domain an assessment sent not answered. */
export type NotAnsweredInDomain = {
  group: string;
  title: string;
  count: number;
};

/** A summary, or why there cannot be one. */
export type SummaryResult =
  | { kind: "summarised"; summary: AssessmentSummaryView }
  | { kind: "uncategorised" };

/**
 * Which ratings each kind of domain shows in its column, and what an item
 * leaving the column has done.
 *
 * Haven's reading of CANS's action levels: a need or a caregiver's need calls
 * for action at 2 or 3, a strength wants building at 2 or 3, and anything in a
 * history above 0 is worth knowing.
 */
const READINGS: Record<
  GroupCategory,
  { shows: (rating: number) => boolean; departure: ItemChange }
> = {
  needs: { shows: (rating) => rating >= 2, departure: "resolved" },
  strengths: { shows: (rating) => rating >= 2, departure: "built" },
  history: { shows: (rating) => rating >= 1, departure: "changed" },
  caregiver: { shows: (rating) => rating >= 2, departure: "resolved" },
};

/** Whether a summary shows an item so rated in its kind of domain's column. */
export const showsInSummary = ({
  category,
  rating,
}: {
  category: GroupCategory;
  rating: number;
}): boolean => READINGS[category].shows(rating);

/**
 * Whether an item so rated calls for action in a plan: whatever its column
 * shows, except in a history, which is context for the rest.
 */
export const callsForAction = ({
  category,
  rating,
}: {
  category: GroupCategory;
  rating: number;
}): boolean => category !== "history" && showsInSummary({ category, rating });

/**
 * Where each kind of domain's column comes: what happened to the young person
 * first, as the context for the needs and strengths beside it.
 */
const COLUMN_ORDER: Record<GroupCategory, number> = {
  history: 0,
  needs: 1,
  strengths: 2,
  caregiver: 3,
};

/** Each group's category, or undefined for an instrument that declares none. */
export const categoriesOf = (
  schema: PublishedInstrumentSchema,
): ReadonlyMap<string, GroupCategory> | undefined => {
  const categories = new Map(
    Object.entries(schema.properties).flatMap(([group, groupSchema]) => {
      const category = groupSchema[CATEGORY_KEYWORD];

      return category === undefined ? [] : [[group, category] as const];
    }),
  );

  return categories.size === 0 ? undefined : categories;
};

/** An item an assessment rated, and the category of its domain. */
type Rated = {
  category: GroupCategory;
  entry: ShownItem;
};

/**
 * Every item an assessment rated with a number, titled and placed as our form
 * asks it. A comment is never rated, and a string response such as
 * `not_applicable` is a fact rather than a rating, so neither is counted.
 *
 * An item our form does not ask is still read, after the form's own and
 * titled by its key, since a submission is a record and nothing in it may be
 * dropped silently.
 */
const ratedIn = (
  { schema, form, responses }: SummaryInput,
  categories: ReadonlyMap<string, GroupCategory>,
): Map<string, Rated> => {
  const domainTitles = new Map(
    form.flatMap((element) =>
      element.kind === "group"
        ? [[element.at.group, element.presentation.title.text] as const]
        : [],
    ),
  );
  const asked = new Map(
    form.flatMap((element, position) =>
      element.kind === "item"
        ? [
            [
              element.at.item,
              {
                title: element.presentation.title.text,
                position,
                scale: scaleOfControl(element.presentation.control),
              },
            ] as const,
          ]
        : [],
    ),
  );

  return new Map(
    responses.flatMap((response): [string, Rated][] => {
      const { group, item } = response;
      const itemSchema = schema.properties[group]?.properties[item];
      const category = categories.get(group);

      if (
        typeof response.response !== "number" ||
        category === undefined ||
        (itemSchema !== undefined && commentTargetOf(itemSchema) !== undefined)
      ) {
        return [];
      }

      const placed = asked.get(item);

      return [
        [
          item,
          {
            category,
            entry: {
              group,
              item,
              title: placed?.title ?? item,
              domain: domainTitles.get(group) ?? group,
              position: placed?.position ?? form.length,
              scale: placed?.scale ?? "rating",
              rating: response.response,
            },
          },
        ],
      ];
    }),
  );
};

/**
 * The items an assessment sent not answered, by item key, with the domains
 * they are in counted in our form's order, and a domain our form does not
 * ask after them.
 */
const notAnsweredIn = ({
  form,
  responses,
}: Pick<SummaryInput, "form" | "responses">): {
  items: ReadonlySet<string>;
  byDomain: NotAnsweredInDomain[];
} => {
  const items = responses.filter(({ response }) => isNotAnswered(response));
  const counts = new Map<string, number>();

  for (const { group } of items) {
    counts.set(group, (counts.get(group) ?? 0) + 1);
  }

  const headings = form.flatMap((element) =>
    element.kind === "group" ? [element] : [],
  );
  const placed = headings.flatMap((heading) => {
    const count = counts.get(heading.at.group);

    return count === undefined
      ? []
      : [
          {
            group: heading.at.group,
            title: heading.presentation.title.text,
            count,
          },
        ];
  });
  const unplaced = [...counts]
    .filter(([group]) => !headings.some(({ at }) => at.group === group))
    .map(([group, count]) => ({ group, title: group, count }));

  return {
    items: new Set(items.map(({ item }) => item)),
    byDomain: [...placed, ...unplaced],
  };
};

/** An item an assessment rated with a number, and the kind of its domain. */
export type RatedItem = ShownItem & { category: GroupCategory };

/**
 * Every item an assessment rated with a number, as `ratedIn` reads them, or
 * undefined where its instrument declares no kinds of domain.
 */
export const ratedItemsOf = (input: SummaryInput): RatedItem[] | undefined => {
  const categories = categoriesOf(input.schema);

  return categories === undefined
    ? undefined
    : [...ratedIn(input, categories).values()].map(({ category, entry }) => ({
        ...entry,
        category,
      }));
};

/** Highest `levelOf` first, then as our form asks them. */
const byRatingThenPosition = (left: ShownItem, right: ShownItem) =>
  levelOf(right) - levelOf(left) || left.position - right.position;

const changeOf = (
  category: GroupCategory,
  rating: number,
  previous: number | undefined,
): ItemChange => {
  const { shows } = READINGS[category];

  if (previous === undefined || !shows(previous)) {
    return "new";
  }

  return match(category)
    .with("history", () => (previous === rating ? "same" : "changed"))
    .with("needs", "strengths", "caregiver", () => {
      if (rating === previous) {
        return "continuing";
      }

      if (rating < previous) {
        return "eased";
      }

      return category === "strengths" ? "weakened" : "escalated";
    })
    .exhaustive();
};

/** What this assessment did with an item the earlier one showed and it does not. */
type DepartedNow = "rated" | "not_answered" | "not_rated";

const nowOf = ({
  rated,
  notAnswered,
}: {
  rated: boolean;
  notAnswered: boolean;
}): DepartedNow => {
  if (rated) {
    return "rated";
  }

  return notAnswered ? "not_answered" : "not_rated";
};

/** How an item the earlier assessment showed, and this one does not, moved. */
const departedChange = ({
  now,
  departure,
}: {
  now: DepartedNow;
  /** How an item rated below its column now left it. */
  departure: ItemChange;
}): ItemChange =>
  match(now)
    .with("rated", () => departure)
    .with("not_answered", (): ItemChange => "not_answered")
    .with("not_rated", (): ItemChange => "not_rated")
    .exhaustive();

/** The items one assessment rated in a category, rated as `keep` asks. */
const ratedWhere = (
  rated: ReadonlyMap<string, Rated>,
  category: GroupCategory,
  keep: (rating: number) => boolean,
): ShownItem[] =>
  [...rated.values()]
    .filter((each) => each.category === category && keep(each.entry.rating))
    .map(({ entry }) => entry)
    .sort(byRatingThenPosition);

/**
 * An assessment's answers laid out by what they call for, and, given an
 * earlier assessment, how each item moved since.
 *
 * The categories are the instruments' own: this one's for every group it has,
 * and the earlier one's for a group only it has. An item is matched across
 * the two by its key, which means one thing throughout a family. There is no
 * summary where this assessment's instrument declares no categories, since
 * nothing then says which column anything belongs in.
 */
export const summaryOf = (
  current: SummaryInput,
  previous?: SummaryInput,
): SummaryResult => {
  const currentCategories = categoriesOf(current.schema);

  if (currentCategories === undefined) {
    return { kind: "uncategorised" };
  }

  const categories = new Map([
    ...(previous === undefined ? [] : (categoriesOf(previous.schema) ?? [])),
    ...currentCategories,
  ]);
  const now = ratedIn(current, categories);
  const before =
    previous === undefined ? undefined : ratedIn(previous, categories);
  const notAnsweredNow = notAnsweredIn(current);
  const notAnsweredBefore =
    previous === undefined ? undefined : notAnsweredIn(previous).items;
  const present = new Set(categories.values());

  const columns = GROUP_CATEGORIES.filter((category) => present.has(category))
    .toSorted((left, right) => COLUMN_ORDER[left] - COLUMN_ORDER[right])
    .map((category): SummaryColumn => {
      const { shows, departure } = READINGS[category];
      const shown = ratedWhere(now, category, shows);

      if (before === undefined) {
        return { category, shown, departed: [] };
      }

      const earlier = ratedWhere(before, category, shows);
      const departed = earlier
        .filter(({ item }) => !shown.some((each) => each.item === item))
        .map((each): DepartedItem => {
          const rated = now.get(each.item)?.entry;

          return {
            // Placed and titled as this assessment asks it, where it does.
            ...(rated ?? each),
            rating: rated?.rating,
            previous: each.rating,
            change: departedChange({
              now: nowOf({
                rated: rated !== undefined,
                notAnswered: notAnsweredNow.items.has(each.item),
              }),
              departure,
            }),
          };
        })
        .sort(
          (left, right) =>
            levelOf({ scale: right.scale, rating: right.previous }) -
              levelOf({ scale: left.scale, rating: left.previous }) ||
            left.position - right.position,
        );

      return {
        category,
        shown: shown.map((each) => {
          const was = before.get(each.item)?.entry.rating;

          return {
            ...each,
            previous: was,
            change: changeOf(category, each.rating, was),
            ...(notAnsweredBefore?.has(each.item) === true
              ? { notAnsweredBefore: true }
              : {}),
          };
        }),
        departed,
        previousCount: earlier.length,
      };
    });

  return {
    kind: "summarised",
    summary: {
      comparing: before !== undefined,
      columns,
      background: ratedWhere(now, "needs", (rating) => rating === 1),
      assets: ratedWhere(now, "strengths", (rating) => rating <= 1),
      caregiverResources: ratedWhere(now, "caregiver", (rating) => rating <= 1),
      rated: now.size,
      notAnsweredByDomain: notAnsweredNow.byDomain,
    },
  };
};

/** How a column's items are grouped into sections. */
export type SummaryGrouping = "rating" | "domain";

export type SummarySection = {
  /** Unique within its column. */
  key: string;
  heading:
    | { kind: "rating"; rating: number; scale: RatingScale }
    | { kind: "domain"; title: string }
    | { kind: "departed" };
  items: SummaryItem[];
};

/**
 * A column's items in sections: by rating, highest first, or by domain in our
 * form's order. What a comparison no longer shows comes last, in a section of
 * its own, whichever grouping is chosen.
 */
export const sectionsOf = (
  column: SummaryColumn,
  grouping: SummaryGrouping,
): SummarySection[] => {
  const sections = new Map<string, SummarySection>();

  for (const each of column.shown) {
    const key =
      grouping === "rating"
        ? `rating-${each.scale}-${String(each.rating)}`
        : each.group;
    const section = sections.get(key) ?? {
      key,
      heading:
        grouping === "rating"
          ? { kind: "rating" as const, rating: each.rating, scale: each.scale }
          : { kind: "domain" as const, title: each.domain },
      items: [],
    };

    section.items.push(each);
    sections.set(key, section);
  }

  const ordered = [...sections.values()];

  if (grouping === "domain") {
    ordered.sort(
      (left, right) =>
        Math.min(...left.items.map(({ position }) => position)) -
        Math.min(...right.items.map(({ position }) => position)),
    );
  }

  return column.departed.length === 0
    ? ordered
    : [
        ...ordered,
        {
          key: "departed",
          heading: { kind: "departed" },
          items: column.departed,
        },
      ];
};

/** How many of a column's items moved each way. */
export const tallyOf = (
  column: SummaryColumn,
): Readonly<Record<ItemChange, number>> => {
  const tally: Record<ItemChange, number> = {
    new: 0,
    escalated: 0,
    eased: 0,
    weakened: 0,
    continuing: 0,
    resolved: 0,
    built: 0,
    same: 0,
    changed: 0,
    not_rated: 0,
    not_answered: 0,
  };

  for (const { change } of [...column.shown, ...column.departed]) {
    if (change !== undefined) {
      tally[change] += 1;
    }
  }

  return tally;
};
